/**
 * WHICH SECTIONS THIS DEVICE HAS ACTUALLY BEEN INTO.
 *
 * The guided flow has to answer one question the engines cannot: *has the
 * surveyor been shown this part of the job at all?* That is not the same as
 * "is it answered". A locked-out pit is walked and empty; a second machine
 * room nobody knew about is unwalked and empty, and they look identical in the
 * document. The design authority's feedback is specifically about the second
 * one — "the survey allows users to close and submit before the second machine
 * room has been surveyed" — so the flow needs the distinction.
 *
 * ── Why this is device-local and NOT in the document ────────────────────────
 * Three reasons, in order of weight:
 *
 *  1. It is not survey data. Nothing the office reads, prices, or estimates
 *     from depends on which screens a thumb visited. Putting it in the document
 *     would push a revision — and therefore an outbox row and a sync — for the
 *     act of *looking*, which is a lot of network for a UI affordance.
 *  2. `SurveyDocument` is the append-only record brief §4 seals on submission.
 *     Adding a field that changes on navigation would make "the document
 *     changed" stop meaning "the survey changed".
 *  3. It is honest about what it knows. "This phone showed you machine room 2"
 *     is a fact about this phone, and that is exactly the claim the gate makes.
 *
 * ── It is never the ONLY evidence ───────────────────────────────────────────
 * `guide.ts` treats a section as walked when it is recorded here **or** when
 * the engines already say something happened in it (any answer, any flag) —
 * see `isWalked`. So a wiped localStorage, a private-mode browser, or a
 * document adopted from another device degrades to "we can tell from the
 * answers", never to "you can never submit". `storage()` returning null is a
 * supported state, not an error: the in-memory fallback still carries a single
 * session, and answered work still counts.
 *
 * Everything below is safe to call on a server or in a node test: `window` is
 * checked, and every storage call is wrapped — a quota error or a blocked
 * cookie jar must not throw on a navigation.
 */

import { useSyncExternalStore } from 'react';

import { sectionInstanceId } from '../../core/types';
import type { Route } from '../routing';

const PREFIX = 'upsurv2.walked.';

/**
 * The last value handed out per survey.
 *
 * `useSyncExternalStore` compares snapshots with `Object.is`, so the getter has
 * to return the SAME Set until something actually changes. Rebuilding it from
 * JSON on every render would re-render forever.
 */
const cache = new Map<string, ReadonlySet<string>>();
/** Survives a storage that refuses to persist. Lost on reload, and that is fine. */
const memory = new Map<string, Set<string>>();
const listeners = new Set<() => void>();

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    // Private mode, a blocked cookie jar, an embedded webview. Not an error.
    return null;
  }
}

function load(surveyId: string): ReadonlySet<string> {
  const cached = cache.get(surveyId);
  if (cached) return cached;

  const set = new Set<string>(memory.get(surveyId) ?? []);
  const store = storage();
  if (store) {
    try {
      const raw = store.getItem(PREFIX + surveyId);
      if (raw !== null) {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          for (const entry of parsed) if (typeof entry === 'string') set.add(entry);
        }
      }
    } catch {
      // A hand-edited or truncated value reads as "nothing walked yet", which
      // over-reports work remaining. That is the direction to fail in.
    }
  }

  cache.set(surveyId, set);
  return set;
}

/** Every section instance this device has opened, for one survey. */
export function walkedSections(surveyId: string): ReadonlySet<string> {
  return load(surveyId);
}

/** Record that the surveyor has been shown a section. Idempotent. */
export function markWalked(surveyId: string, sectionId: string): void {
  const current = load(surveyId);
  if (current.has(sectionId)) return;

  const next = new Set(current);
  next.add(sectionId);
  cache.set(surveyId, next);

  const mem = memory.get(surveyId) ?? new Set<string>();
  mem.add(sectionId);
  memory.set(surveyId, mem);

  const store = storage();
  if (store) {
    try {
      store.setItem(PREFIX + surveyId, JSON.stringify([...next]));
    } catch {
      // Out of quota. The in-memory copy still carries this session.
    }
  }

  for (const listener of listeners) listener();
}

/** Drop a survey's trail — for the device-local delete and discard paths. */
export function forgetWalked(surveyId: string): void {
  cache.delete(surveyId);
  memory.delete(surveyId);
  const store = storage();
  if (store) {
    try {
      store.removeItem(PREFIX + surveyId);
    } catch {
      // Nothing to do and nothing to say: the survey is going away anyway.
    }
  }
  for (const listener of listeners) listener();
}

/**
 * Drop one room's trail, for the survey it belongs to — the walked-side half
 * of `applyRoomCount` dropping a machine room (D-037 final review).
 *
 * `mr-slot-N` ids are deterministic (`buildingSetup.ts`'s `freeRoomIds`), so a
 * room dropped by lowering B12 and re-created later by raising it again can
 * land on the SAME id. `applyRoomCount` already clears that room's answers,
 * notes, photos and gaps from the document, but this trail lives beside the
 * document, on the device, and nothing else was clearing it — a re-created
 * room could inherit a stranger's walked entries, and `guide.ts`'s `isWalked`
 * (which trusts this set for any section the engines cannot judge on their
 * own, e.g. Room Setup) could offer Submit on a room nobody has ever opened.
 * The prefix matches `buildingSetup.ts`'s own `roomPrefix` — every section
 * instance id scoped to this room starts `room:${roomId}|` — so this stays a
 * plain string match, not an import of a function that file does not export.
 */
export function forgetWalkedUnder(surveyId: string, roomId: string): void {
  const prefix = `room:${roomId}|`;
  const current = load(surveyId);
  const next = new Set([...current].filter((sectionId) => !sectionId.startsWith(prefix)));
  if (next.size === current.size) return;

  cache.set(surveyId, next);
  memory.set(surveyId, new Set(next));

  const store = storage();
  if (store) {
    try {
      store.setItem(PREFIX + surveyId, JSON.stringify([...next]));
    } catch {
      // Out of quota. The in-memory copy still carries the removal.
    }
  }

  for (const listener of listeners) listener();
}

/**
 * The section a route puts on screen, or null when the route is not a section.
 *
 * Pure, and the reason the shell can record a visit without knowing anything
 * about screens. Room setup earns an entry because the `roomSetup` route IS
 * the `room_setup` section — since 2026-08-18 it renders the very same section
 * screen the walk's `/section/` route does, but a surveyor who reaches it from
 * the building screen arrives by THIS route, so without this line a fully
 * set-up room reached that way would read as never walked.
 */
export function walkedSectionForRoute(route: Route): { surveyId: string; sectionId: string } | null {
  switch (route.name) {
    case 'section':
      return { surveyId: route.surveyId, sectionId: route.sectionId };
    case 'roomSetup':
      return {
        surveyId: route.surveyId,
        sectionId: sectionInstanceId('room_setup', { roomId: route.roomId }),
      };
    case 'building':
      return { surveyId: route.surveyId, sectionId: sectionInstanceId('building') };
    default:
      return null;
  }
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/** The walked set for one survey, re-rendering when it grows. */
export function useWalkedSections(surveyId: string): ReadonlySet<string> {
  return useSyncExternalStore(
    subscribe,
    () => walkedSections(surveyId),
    () => walkedSections(surveyId),
  );
}
